// Karte „Regulierung“ der Schadenmappe: die Schadenabrechnung nach der
// Excel-Vorlage „Abrechnung Schaden – Vorlage.xlsx“. Rechnungen links,
// Regulierungsanspruch des Versicherers rechts, darunter die Abzüge.
// Jede Zelle speichert beim Verlassen des Feldes; gerechnet wird sofort lokal
// (`berechneAbrechnung`), der Server bleibt maßgeblich.
import * as React from "react";
import type { MappeCostRow, MappeDamage, SchadenmappeData } from "../types";
import { createCostRow, deleteCostRow, updateCostRow, updateDamage } from "../api";
import {
  STEUER_STANDARD_PCT,
  berechneAbrechnung,
  formatBetragEingabe,
  formatEuro,
  parseEingabe,
} from "./abrechnung";
import "./regulierung.css";

type TextFeld = "issuer" | "invoiceNumber" | "shortText" | "grund" | "paidTo";
type DatumFeld = "invoiceDate" | "paymentDate";
type BetragFeld = "invoiceNet" | "deductionGross" | "paymentGross";
type Feld = TextFeld | DatumFeld | BetragFeld;
/** Eine Tabellenzeile, wie sie im Formular steht: alles als Text. */
type Zeile = { id: number; pos: number } & Record<Feld, string>;

type ParamFeld = "versSteuerPct" | "vorsteuerabzugPct" | "sbFix" | "sbProzent";
type Parameter = Record<ParamFeld, string> & { vorsteuerabzug: boolean };

type Speicherstatus = "idle" | "laeuft" | "ok" | "fehler";

const TEXT_FELDER: TextFeld[] = ["issuer", "invoiceNumber", "shortText", "grund", "paidTo"];
const DATUM_FELDER: DatumFeld[] = ["invoiceDate", "paymentDate"];
const BETRAG_FELDER: BetragFeld[] = ["invoiceNet", "deductionGross", "paymentGross"];
const ALLE_FELDER: Feld[] = [...TEXT_FELDER, ...DATUM_FELDER, ...BETRAG_FELDER];
const PARAM_FELDER: ParamFeld[] = ["versSteuerPct", "vorsteuerabzugPct", "sbFix", "sbProzent"];

/** Spaltenbreiten der Datenbank (`damageCostRow.entity.ts`). */
const MAX_LAENGE: Record<TextFeld, number> = {
  issuer: 255,
  invoiceNumber: 100,
  shortText: 255,
  grund: 255,
  paidTo: 64,
};

function zahl(wert: unknown): number {
  const n = Number(wert ?? 0);
  return Number.isFinite(n) ? n : 0;
}

/** Betrag fürs Feld; 0 bleibt leer, damit die Tabelle nicht voller Nullen steht. */
function betragText(wert: unknown): string {
  const n = zahl(wert);
  return n === 0 ? "" : formatBetragEingabe(n);
}

/** Prozent-/Betragsparameter fürs Feld: ohne erzwungene Nachkommastellen. */
function paramText(wert: unknown): string {
  if (wert === null || wert === undefined || wert === "") return "";
  const n = Number(wert);
  return Number.isFinite(n) ? String(n).replace(".", ",") : "";
}

function zeileAusServer(row: MappeCostRow): Zeile {
  return {
    id: row.id,
    pos: row.pos ?? 0,
    issuer: row.issuer ?? "",
    invoiceNumber: row.invoiceNumber ?? "",
    shortText: row.shortText ?? "",
    grund: row.grund ?? "",
    paidTo: row.paidTo ?? "",
    invoiceDate: row.invoiceDate ? String(row.invoiceDate).slice(0, 10) : "",
    paymentDate: row.paymentDate ? String(row.paymentDate).slice(0, 10) : "",
    invoiceNet: betragText(row.invoiceNet),
    deductionGross: betragText(row.deductionGross),
    paymentGross: betragText(row.paymentGross),
  };
}

function parameterAusServer(damage: MappeDamage): Parameter {
  return {
    versSteuerPct: paramText(damage.versSteuerPct ?? STEUER_STANDARD_PCT),
    vorsteuerabzug: damage.vorsteuerabzug === true,
    vorsteuerabzugPct: paramText(damage.vorsteuerabzugPct),
    sbFix: zahl(damage.sbFix) === 0 ? "" : formatBetragEingabe(zahl(damage.sbFix)),
    sbProzent: zahl(damage.sbProzent) === 0 ? "" : paramText(damage.sbProzent),
  };
}

function sortiert(rows: MappeCostRow[]): MappeCostRow[] {
  return [...rows].sort((a, b) => (a.pos ?? 0) - (b.pos ?? 0) || a.id - b.id);
}

function datumDe(iso: string | null | undefined): string {
  if (!iso) return "–";
  const [j, m, t] = String(iso).slice(0, 10).split("-");
  return j && m && t ? `${t}.${m}.${j}` : "–";
}

function hatInhalt(z: Zeile): boolean {
  return ALLE_FELDER.some((f) => z[f].trim() !== "");
}

export function RegulierungBereich(props: { data: SchadenmappeData; onReload: () => void | Promise<void> }) {
  const { data, onReload } = props;
  const damage = data.damage;
  const damageId = damage.id;

  const [zeilen, setZeilen] = React.useState<Zeile[]>(() => sortiert(data.costRows).map(zeileAusServer));
  const [param, setParam] = React.useState<Parameter>(() => parameterAusServer(damage));
  const [status, setStatus] = React.useState<Speicherstatus>("idle");
  const [loeschFrage, setLoeschFrage] = React.useState<number | null>(null);

  /** Felder mit ungespeicherter Eingabe („<zeilenId>.<feld>“ bzw. „p.<feld>“). */
  const dirty = React.useRef(new Set<string>());
  /** Laufende Speicheraufrufe; solange > 0, überschreibt kein Reload die Eingaben. */
  const offen = React.useRef(0);
  /** Fehlgeschlagene Speicheraufrufe für „Wiederholen“. */
  const fehlgeschlagen = React.useRef(new Map<string, () => Promise<unknown>>());
  const onReloadRef = React.useRef(onReload);
  onReloadRef.current = onReload;

  // Serverstand übernehmen — außer in Feldern, in denen gerade Ungespeichertes steht.
  React.useEffect(() => {
    if (offen.current > 0) return;
    setZeilen((alt) =>
      sortiert(data.costRows).map((row) => {
        const neu = zeileAusServer(row);
        const lokal = alt.find((z) => z.id === row.id);
        if (lokal) {
          for (const f of ALLE_FELDER) if (dirty.current.has(`${row.id}.${f}`)) neu[f] = lokal[f];
        }
        return neu;
      }),
    );
    setParam((alt) => {
      const neu = parameterAusServer(data.damage);
      for (const f of PARAM_FELDER) if (dirty.current.has(`p.${f}`)) neu[f] = alt[f];
      return neu;
    });
  }, [data]);

  const speichere = React.useCallback(async (key: string, aufruf: () => Promise<unknown>) => {
    offen.current += 1;
    setStatus("laeuft");
    let ok = true;
    try {
      await aufruf();
      dirty.current.delete(key);
      fehlgeschlagen.current.delete(key);
    } catch {
      ok = false;
      fehlgeschlagen.current.set(key, aufruf);
    }
    offen.current -= 1;
    if (offen.current > 0) return;
    if (!ok || fehlgeschlagen.current.size > 0) {
      setStatus("fehler");
      return;
    }
    setStatus("ok");
    try {
      await onReloadRef.current();
    } catch {
      /* Der Wert ist gespeichert; ein misslungenes Nachladen ist kein Speicherfehler. */
    }
  }, []);

  const wiederholen = () => {
    const liste = [...fehlgeschlagen.current.entries()];
    fehlgeschlagen.current.clear();
    for (const [key, aufruf] of liste) void speichere(key, aufruf);
  };

  // ---------- Tabellenzellen ----------
  const aendere = (id: number, feld: Feld, wert: string) => {
    dirty.current.add(`${id}.${feld}`);
    setZeilen((alt) => alt.map((z) => (z.id === id ? { ...z, [feld]: wert } : z)));
  };

  const verlasse = (id: number, feld: Feld, wert: string) => {
    const key = `${id}.${feld}`;
    if (!dirty.current.has(key)) return;
    let patch: Partial<MappeCostRow>;
    if ((BETRAG_FELDER as Feld[]).includes(feld)) {
      const n = Math.max(0, parseEingabe(wert) ?? 0);
      // Anzeige glätten („1250,5“ → „1.250,50“); der Fokus ist ohnehin schon weg.
      setZeilen((alt) => alt.map((z) => (z.id === id ? { ...z, [feld]: betragText(n) } : z)));
      patch = { [feld]: n };
    } else if ((DATUM_FELDER as Feld[]).includes(feld)) {
      patch = { [feld]: wert || null };
    } else {
      patch = { [feld]: wert.trim() || null };
    }
    void speichere(key, () => updateCostRow(damageId, id, patch));
  };

  const neueZeile = () => {
    const pos = zeilen.reduce((max, z) => Math.max(max, z.pos), 0) + 1;
    void speichere(`neu.${pos}.${Date.now()}`, async () => {
      const row = await createCostRow(damageId, { pos });
      setZeilen((alt) => (alt.some((z) => z.id === row.id) ? alt : [...alt, zeileAusServer({ ...row, pos: row.pos ?? pos })]));
    });
  };

  const loesche = (id: number) => {
    setLoeschFrage(null);
    void speichere(`weg.${id}`, async () => {
      await deleteCostRow(damageId, id);
      for (const f of ALLE_FELDER) {
        dirty.current.delete(`${id}.${f}`);
        fehlgeschlagen.current.delete(`${id}.${f}`);
      }
      setZeilen((alt) => alt.filter((z) => z.id !== id));
    });
  };

  // ---------- Parameter (Steuersatz, Vorsteuer, Selbstbeteiligung) ----------
  const aendereParam = (feld: ParamFeld, wert: string) => {
    dirty.current.add(`p.${feld}`);
    setParam((alt) => ({ ...alt, [feld]: wert }));
  };

  const verlasseParam = (feld: ParamFeld, wert: string) => {
    const key = `p.${feld}`;
    if (!dirty.current.has(key)) return;
    const gelesen = parseEingabe(wert);
    let n = gelesen === null ? null : Math.max(0, gelesen);
    // Der Steuersatz ist in der Datenbank Pflicht: leer heißt Standard (19 %).
    if (feld === "versSteuerPct" && n === null) {
      n = STEUER_STANDARD_PCT;
      setParam((alt) => ({ ...alt, versSteuerPct: paramText(STEUER_STANDARD_PCT) }));
    }
    if (feld === "sbFix") setParam((alt) => ({ ...alt, sbFix: n ? formatBetragEingabe(n) : "" }));
    void speichere(key, () => updateDamage(damageId, { [feld]: n }));
  };

  const setzeVorsteuer = (ja: boolean) => {
    if (param.vorsteuerabzug === ja) return;
    setParam((alt) => ({ ...alt, vorsteuerabzug: ja }));
    void speichere("p.vorsteuerabzug", () => updateDamage(damageId, { vorsteuerabzug: ja }));
  };

  // ---------- Rechnen ----------
  const steuerEingabe = parseEingabe(param.versSteuerPct);
  const abrechnung = berechneAbrechnung(
    {
      versSteuerPct: steuerEingabe,
      vorsteuerabzug: param.vorsteuerabzug,
      vorsteuerabzugPct: parseEingabe(param.vorsteuerabzugPct),
      sbFix: parseEingabe(param.sbFix),
      sbProzent: parseEingabe(param.sbProzent),
      claimAmount: damage.claimAmount,
    },
    zeilen.map((z) => ({
      invoiceNet: parseEingabe(z.invoiceNet),
      deductionGross: parseEingabe(z.deductionGross),
      paymentGross: parseEingabe(z.paymentGross),
    })),
  );
  const faktor = 1 + (abrechnung.steuerPct ?? STEUER_STANDARD_PCT) / 100;
  const ausgeglichen = Math.abs(abrechnung.openPayments) < 0.005;

  // ---------- Bausteine ----------
  const textZelle = (z: Zeile, nr: number, feld: TextFeld, name: string, klasse = "") => (
    <td className={klasse}>
      <input
        type="text"
        className="smr-zelle"
        aria-label={`Rechnung ${nr} ${name}`}
        maxLength={MAX_LAENGE[feld]}
        value={z[feld]}
        onChange={(e) => aendere(z.id, feld, e.target.value)}
        onBlur={(e) => verlasse(z.id, feld, e.target.value)}
      />
    </td>
  );
  const datumZelle = (z: Zeile, nr: number, feld: DatumFeld, name: string) => (
    <td>
      <input
        type="date"
        className="smr-zelle smr-zelle-datum"
        aria-label={`Rechnung ${nr} ${name}`}
        value={z[feld]}
        onChange={(e) => aendere(z.id, feld, e.target.value)}
        onBlur={(e) => verlasse(z.id, feld, e.target.value)}
      />
    </td>
  );
  const betragZelle = (z: Zeile, nr: number, feld: BetragFeld, name: string, klasse = "") => (
    <td className={`smr-r ${klasse}`}>
      <input
        type="text"
        inputMode="decimal"
        className="smr-zelle smr-zelle-betrag"
        aria-label={`Rechnung ${nr} ${name}`}
        placeholder="0,00"
        value={z[feld]}
        onChange={(e) => aendere(z.id, feld, e.target.value)}
        onBlur={(e) => verlasse(z.id, feld, e.target.value)}
      />
    </td>
  );
  const paramEingabe = (feld: ParamFeld, name: string, deaktiviert = false) => (
    <input
      type="text"
      inputMode="decimal"
      className="smr-param"
      aria-label={name}
      disabled={deaktiviert}
      value={param[feld]}
      onChange={(e) => aendereParam(feld, e.target.value)}
      onBlur={(e) => verlasseParam(feld, e.target.value)}
    />
  );
  const kopfWert = (titel: string, wert: React.ReactNode) => (
    <div className="smr-kopf-feld">
      <span className="smr-lbl">{titel}</span>
      <span className="smr-wert">{wert || "–"}</span>
    </div>
  );

  return (
    <section className="smr-karte" aria-label="Regulierung">
      <header className="smr-titelzeile">
        <h2 className="smr-titel">Regulierung</h2>
        <span className="smr-untertitel">Schadenabrechnung</span>
        <div className="smr-status" role="status" aria-live="polite">
          {status === "laeuft" ? <span className="smr-status-laeuft">speichert…</span> : null}
          {status === "ok" ? <span className="smr-status-ok">gespeichert ✓</span> : null}
          {status === "fehler" ? (
            <button type="button" className="smr-status-fehler" onClick={wiederholen}>
              Speichern fehlgeschlagen – Wiederholen
            </button>
          ) : null}
        </div>
      </header>

      <div className="smr-kopf">
        {kopfWert("Versicherungsnehmer", damage.contact?.displayName)}
        {kopfWert("Vertragsnummer", damage.contract?.contractNbr)}
        {kopfWert("Schadennr. Versicherer", damage.externalDamageNbr)}
        {kopfWert("Schadennr. intern (Courté)", damage.damageNbr)}
        {kopfWert("Sparte", damage.contract?.branch)}
        {kopfWert("Schadenart", damage.damageType)}
        {kopfWert("Schadentag", datumDe(damage.damageDate))}
        {kopfWert("Stand", new Date().toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric" }))}
        <label className="smr-kopf-feld">
          <span className="smr-lbl">Steuersatz in %</span>
          {paramEingabe("versSteuerPct", "Steuersatz in Prozent")}
        </label>
      </div>

      <div className="smr-tw">
        <table className="smr-tabelle">
          <thead>
            <tr className="smr-gruppen">
              <th colSpan={4}>Rechnungen</th>
              <th colSpan={2}>Kosten lt. Rechnungen</th>
              <th colSpan={6} className="smr-gr">Regulierungsanspruch laut Versicherer</th>
              <th />
            </tr>
            <tr>
              <th>Rechnungsaussteller</th>
              <th>Rg-Nummer</th>
              <th>Rg-Datum</th>
              <th>Kurzbeschreibung</th>
              <th className="smr-r">Nettobetrag</th>
              <th className="smr-r">Bruttobetrag</th>
              <th className="smr-r smr-gr">Kürzungen</th>
              <th>Grund</th>
              <th className="smr-r">Kosten</th>
              <th className="smr-r">Zahlungshöhe</th>
              <th>Datum Zahlung</th>
              <th>Zahlung an</th>
              <th aria-label="Aktionen" />
            </tr>
          </thead>
          <tbody>
            {zeilen.length === 0 ? (
              <tr>
                <td colSpan={13} className="smr-leer">
                  Noch keine Rechnung erfasst.
                </td>
              </tr>
            ) : null}
            {zeilen.map((z, i) => {
              const nr = i + 1;
              const brutto = zahl(parseEingabe(z.invoiceNet)) * faktor;
              const kosten = Math.max(0, brutto - zahl(parseEingabe(z.deductionGross)));
              return (
                <tr key={z.id}>
                  {textZelle(z, nr, "issuer", "Rechnungsaussteller")}
                  {textZelle(z, nr, "invoiceNumber", "Rechnungsnummer")}
                  {datumZelle(z, nr, "invoiceDate", "Rechnungsdatum")}
                  {textZelle(z, nr, "shortText", "Kurzbeschreibung")}
                  {betragZelle(z, nr, "invoiceNet", "Nettobetrag")}
                  <td className="smr-calc">{formatEuro(brutto)}</td>
                  {betragZelle(z, nr, "deductionGross", "Kürzungen", "smr-gr")}
                  {textZelle(z, nr, "grund", "Grund der Kürzung")}
                  <td className="smr-calc">{formatEuro(kosten)}</td>
                  {betragZelle(z, nr, "paymentGross", "Zahlungshöhe")}
                  {datumZelle(z, nr, "paymentDate", "Datum der Zahlung")}
                  {textZelle(z, nr, "paidTo", "Zahlung an")}
                  <td className="smr-aktion">
                    {loeschFrage === z.id ? (
                      <span className="smr-loeschfrage">
                        <button type="button" className="smr-knopf smr-knopf-gefahr" onClick={() => loesche(z.id)}>
                          Löschen
                        </button>
                        <button type="button" className="smr-knopf" onClick={() => setLoeschFrage(null)}>
                          Abbrechen
                        </button>
                      </span>
                    ) : (
                      <button
                        type="button"
                        className="smr-ib"
                        aria-label={`Rechnung ${nr} löschen`}
                        title="Zeile löschen"
                        onClick={() => (hatInhalt(z) ? setLoeschFrage(z.id) : loesche(z.id))}
                      >
                        ×
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr>
              <td colSpan={4}>Gesamt</td>
              <td className="smr-r">{formatEuro(abrechnung.invoiceNetSum)}</td>
              <td className="smr-r">{formatEuro(abrechnung.invoiceGrossSum)}</td>
              <td className="smr-r smr-gr">{formatEuro(abrechnung.deductionGrossSum)}</td>
              <td />
              <td className="smr-r">{formatEuro(abrechnung.costsGross)}</td>
              <td className="smr-r">{formatEuro(abrechnung.alreadyPaid)}</td>
              <td colSpan={3} />
            </tr>
          </tfoot>
        </table>
      </div>
      <div>
        <button type="button" className="smr-knopf smr-knopf-neu" onClick={neueZeile}>
          + Rechnung
        </button>
      </div>

      <div className="smr-abz">
        <div className="smr-abz-zeile">
          <span className="smr-abz-n">
            <b>Vorsteuerabzug</b>
          </span>
          <span className="smr-abz-eingabe" role="radiogroup" aria-label="Vorsteuerabzug">
            <label className="smr-radio">
              <input
                type="radio"
                name={`smr-vst-${damageId}`}
                checked={!param.vorsteuerabzug}
                onChange={() => setzeVorsteuer(false)}
              />{" "}
              Nein
            </label>
            <label className="smr-radio">
              <input
                type="radio"
                name={`smr-vst-${damageId}`}
                checked={param.vorsteuerabzug}
                onChange={() => setzeVorsteuer(true)}
              />{" "}
              Ja, zu
            </label>
            {paramEingabe("vorsteuerabzugPct", "Vorsteuerabzug in Prozent", !param.vorsteuerabzug)} %
            {abrechnung.vorsteuerKonflikt ? (
              <span className="smr-fehler" role="alert">
                Prozentsatz fehlt
              </span>
            ) : null}
          </span>
          <span className="smr-abz-b">− {formatEuro(abrechnung.vatDeductionAmount)}</span>
        </div>
        <div className="smr-abz-zeile smr-abz-sum">
          <span className="smr-abz-n">Zwischensumme</span>
          <span className="smr-abz-b">{formatEuro(abrechnung.intermediate)}</span>
        </div>
        <div className="smr-abz-zeile">
          <span className="smr-abz-n">
            <b>Selbstbeteiligung</b>
          </span>
          <span className="smr-abz-eingabe">
            Fix {paramEingabe("sbFix", "Selbstbeteiligung fix in Euro")} € oder{" "}
            {paramEingabe("sbProzent", "Selbstbeteiligung in Prozent")} %
            {abrechnung.sbKonflikt ? (
              <span className="smr-fehler" role="alert">
                Nur eines von beiden – es gilt der feste Betrag
              </span>
            ) : null}
          </span>
          <span className="smr-abz-b">− {formatEuro(abrechnung.sbAmount)}</span>
        </div>
        <div className="smr-abz-zeile smr-abz-end">
          <span className="smr-abz-n">Entschädigungssumme</span>
          <span className="smr-abz-b">{formatEuro(abrechnung.entschaedigung)}</span>
        </div>
        <div className="smr-abz-zeile">
          <span className="smr-abz-n">Bereits geleistete Zahlungen</span>
          <span className="smr-abz-b">{formatEuro(abrechnung.alreadyPaid)}</span>
        </div>
        <div className="smr-abz-zeile smr-abz-sum">
          <span className="smr-abz-n">Offene Zahlungen</span>
          <span className={`smr-abz-b ${ausgeglichen ? "smr-ausgeglichen" : "smr-offen"}`}>
            {formatEuro(abrechnung.openPayments)}
          </span>
        </div>
      </div>
    </section>
  );
}
